import {LocalizationStore} from 'common/i18n/store';
import {FeatureGate, FeatureGateTier, RestrictEntitySaving} from 'common/report/constants';
import {CurrentProject} from 'common/report/model-resources/projects';
import {savedReportLimitInfo} from 'common/report/stores/saved-reports-limit';
import {UpsellType} from 'common/report/util/tracking-helpers';

export interface UpsellInfo {
  title: string;
  description: string;
  upsellType: UpsellType;
  trackingFeatureGate: string; // Connect this to feature gates in iron/common/report/util/tracking-helpers.ts
}

export interface Metadata {
  currentCustomAlerts?: number;
}

type UpsellCheck = (currentProject: CurrentProject, i18n: LocalizationStore, metadata?: Metadata) => UpsellInfo | null;

// Keep these in alphabetical order
export enum ModalTrigger {
  ADVANCED_SHARING = `advanced_sharing`, // DO NOT USE THIS IN AN ACTUAL MODAL, but consolidate data here
  ALERTS = `alerts`,
  COHORT = `cohort`,
  EVENTS = `events`,
  FORMULA = `formula`,
  METRICS = `metrics`,
  SAVE_REPORTS = `saved_report`,
  SPARK = `spark`,
  TESTING = `testing`, // shouldn't happen, but useful for storybook
}

/**
 * This constant encapsulates the logic for determining if a modal should be shown
 * as well as the content of the modal and any metadata.
 */
const TRIGGER_TO_CHECK: Partial<Record<ModalTrigger, UpsellCheck>> = {
  [ModalTrigger.ADVANCED_SHARING]: (currentProject: CurrentProject, i18n: LocalizationStore) => {
    if (!currentProject.canSaveEntity(RestrictEntitySaving.Cohorts)) {
      const {t} = i18n;
      return {
        title: t(`upsells.advanced-sharing.title`),
        description: t(`upsells.advanced-sharing.description`),
        upsellType: UpsellType.FREE_TO_GROWTH,
        trackingFeatureGate: `sharing`,
      };
    }
    return null;
  },
  [ModalTrigger.ALERTS]: (currentProject: CurrentProject, i18n: LocalizationStore, metadata?: Metadata) => {
    const {t} = i18n;
    if (!currentProject.getFeatureGate(FeatureGate.AllowCustomAlerts)) {
      return {
        title: t(`upsells.save-alert.title`),
        description: t(`upsells.save-alert.description`),
        upsellType: UpsellType.FREE_TO_GROWTH,
        trackingFeatureGate: `alerts`,
      };
    }

    const alertLimit = currentProject.getFeatureGate(FeatureGate.MaxCustomAlerts);

    if (alertLimit && metadata?.currentCustomAlerts && metadata.currentCustomAlerts >= alertLimit) {
      return {
        title: t(`upsells.alert-limit.title`),
        description: t(`upsells.alert-limit.description`, {
          alertLimit,
        }),
        upsellType: UpsellType.GROWTH_TO_ENTERPRISE,
        trackingFeatureGate: `alerts`,
      };
    }

    return null;
  },
  [ModalTrigger.COHORT]: (currentProject: CurrentProject, i18n: LocalizationStore) => {
    if (!currentProject.canSaveEntity(RestrictEntitySaving.Cohorts)) {
      const {t} = i18n;
      return {
        title: t(`upsells.save-cohort.title`),
        description: t(`upsells.save-cohort.description`),
        upsellType: UpsellType.FREE_TO_GROWTH,
        trackingFeatureGate: `cohorts`,
      };
    }
    return null;
  },
  [ModalTrigger.TESTING]: (_: CurrentProject) => {
    return {
      title: `Upgrade your plan now!`,
      description: `Upgrade to access all the features below.`,
      upsellType: UpsellType.FREE_TO_GROWTH,
      trackingFeatureGate: ``,
    };
  },
  [ModalTrigger.FORMULA]: (currentProject: CurrentProject, i18n: LocalizationStore) => {
    if (!currentProject.canSaveEntity(RestrictEntitySaving.Formulas)) {
      const {t} = i18n;
      return {
        title: t(`upsells.save-formula.title`),
        description: t(`upsells.save-formula.description`),
        upsellType: UpsellType.FREE_TO_GROWTH,
        trackingFeatureGate: `formulas`,
      };
    }
    return null;
  },
  [ModalTrigger.METRICS]: (currentProject: CurrentProject, i18n: LocalizationStore) => {
    if (!currentProject.canSaveEntity(RestrictEntitySaving.BehaviorMetrics)) {
      const {t} = i18n;
      return {
        title: t(`upsells.save-metric.title`),
        description: t(`upsells.save-metric.description`),
        upsellType: UpsellType.FREE_TO_GROWTH,
        trackingFeatureGate: `saved_metrics`,
      };
    }
    return null;
  },
  [ModalTrigger.SAVE_REPORTS]: (currentProject: CurrentProject, i18n: LocalizationStore) => {
    const {isBelowLimit} = savedReportLimitInfo(currentProject.id);
    if (!isBelowLimit) {
      const {t} = i18n;
      return {
        title: t(`upsells.save-report.title`),
        description: t(`upsells.save-report.description`),
        upsellType: UpsellType.FREE_TO_GROWTH,
        trackingFeatureGate: `bookmark`,
      };
    }
    return null;
  },
  [ModalTrigger.SPARK]: (currentProject: CurrentProject, i18n: LocalizationStore) => {
    // We currently do not have an endpoint and it would be expensive to have to
    // re-call the endpoint everytime we want to check for the modal. Instead,
    // defer to client code to handle this.
    if (!currentProject.isFreeFeatureGateTier()) {
      return null;
    }
    const {t} = i18n;

    return {
      title: t(`upsells.spark.title`),
      description: t(`upsells.spark.description`),
      upsellType: UpsellType.FREE_TO_GROWTH,
      trackingFeatureGate: `bookmark`,
    };
  },
  [ModalTrigger.EVENTS]: (currentProject: CurrentProject, i18n: LocalizationStore) => {
    if (!currentProject.isFreeFeatureGateTier()) {
      return null;
    }
    const {t} = i18n;

    if (currentProject.blocking.isBlockedEvents) {
      return {
        title: t(`upsells.events-limit.title`),
        description: t(`upsells.events-limit.description`),
        upsellType: UpsellType.FREE_TO_GROWTH,
        trackingFeatureGate: `bookmark`,
      };
    }

    return null;
  },
};

const getUpsellInfoForTrigger = (
  currentProject: CurrentProject | undefined,
  modalTrigger: ModalTrigger,
  i18n: LocalizationStore,
  metadata?: Metadata,
): UpsellInfo | null => {
  if (!currentProject) {
    return null;
  }

  const infoCallback = TRIGGER_TO_CHECK[modalTrigger];

  if (!infoCallback) {
    return null;
  }

  return infoCallback(currentProject, i18n, metadata);
};

/**
 * Check if the modal should be rendered based on the current project and modal trigger.
 */
export const getUpsellInfo = (
  currentProject: CurrentProject | undefined,
  modalTrigger: ModalTrigger,
  i18n: LocalizationStore,
  metadata?: Metadata,
): UpsellInfo | null => {
  if (currentProject?.featureGateTier === FeatureGateTier.Enterprise) {
    return null;
  }

  return getUpsellInfoForTrigger(currentProject, modalTrigger, i18n, metadata);
};
